/**
 * Text panel component.
 *
 * Displays reading passage chunks with navigation.
 *
 * @module TextPanel
 */

import { useRef, useEffect, useCallback } from 'react';
import { TextToolbar } from './TextToolbar';
import { TwoColumnContext } from './TwoColumnContext';
import { SimulationChunkNavigation } from './shared/SimulationChunkNavigation';
import { SimulationPanelHeader } from './shared/SimulationPanelHeader';
import { requiresTwoColumnRendering, splitContextBySeparator } from '@/utils/specialContextUtils';
import type { TextChunk, Paragraph, Question, ExamType, CEFRLevel } from '@/types/exam-simulation';
import { cn } from '@/lib/utils';

interface TextPanelProps {
  readonly chunk: TextChunk;
  readonly currentChunkIndex: number;
  readonly totalChunks: number;
  readonly highlightedParagraphId: number | null;
  readonly textSize: 'small' | 'medium' | 'large';
  readonly highlighterActive: boolean;
  readonly questions: Question[];
  readonly examType?: ExamType;
  readonly level?: CEFRLevel;
  readonly highlights: Record<string, { start: number; end: number }[]>;
  readonly onNavigate: (direction: 'prev' | 'next') => void;
  readonly onTextSizeChange: (size: 'small' | 'medium' | 'large') => void;
  readonly onHighlighterToggle: () => void;
  readonly onEraseHighlights: () => void;
  readonly onAddHighlight: (paragraphId: number, start: number, end: number) => void;
  /** When set, question number badges jump to that question in the panel */
  readonly onContextQuestionSelect?: (questionId: number) => void;
  /** ID of the currently active question, used to mark the active badge */
  readonly activeQuestionId?: number | null;
}

export function TextPanel({
  chunk,
  currentChunkIndex,
  totalChunks,
  highlightedParagraphId,
  textSize,
  highlighterActive,
  questions,
  examType,
  level,
  highlights,
  onNavigate,
  onTextSizeChange,
  onHighlighterToggle,
  onEraseHighlights,
  onAddHighlight,
  onContextQuestionSelect,
  activeQuestionId,
}: TextPanelProps) {
  const textSizeClasses = {
    small: 'text-base leading-relaxed',
    medium: 'text-lg leading-relaxed',
    large: 'text-xl leading-loose',
  };

  // Ref for the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Map questions to paragraphs by paragraphRef
  const getQuestionNumbersForParagraph = (paragraphId: number): number[] => {
    return questions
      .filter((q) => q.paragraphRef === paragraphId)
      .map((q) => q.id)
      .sort((a, b) => a - b);
  };

  // Scroll to top when chunk changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [currentChunkIndex]);

  // Auto-scroll to highlighted paragraph when it changes
  useEffect(() => {
    if (!highlightedParagraphId || !scrollContainerRef.current) {
      return;
    }

    // Small delay to ensure DOM is updated after highlight
    const timeoutId = setTimeout(() => {
      const paragraphElement = scrollContainerRef.current?.querySelector(
        `[data-paragraph-id="${highlightedParagraphId}"]`
      );

      if (paragraphElement instanceof HTMLElement) {
        paragraphElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [highlightedParagraphId]);

  return (
    <div className="flex flex-col h-full bg-exam-ivory relative">
      {/* Section header — toolbar lives inside the rightSlot so it stays on
          the same row as the title and never overlaps the prompt text. */}
      <SimulationPanelHeader
        title={chunk.title}
        wrapTitle
        rightSlot={
          <TextToolbar
            textSize={textSize}
            highlighterActive={highlighterActive}
            onTextSizeChange={onTextSizeChange}
            onHighlighterToggle={onHighlighterToggle}
            onEraseHighlights={onEraseHighlights}
          />
        }
        className="px-4 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-border/50 bg-transparent"
      />

      {/* Scrollable Text Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-8 sm:px-12 py-4 sm:py-6 relative">
        <div className={cn(
          "space-y-6 sm:space-y-8 ml-0 sm:ml-8",
          // Use wider max-width for two-column layout if needed
          examType === 'goethe' && level === 'A1' && chunk.id === 2
            ? "max-w-5xl"
            : "max-w-2xl"
        )}>
          {chunk.paragraphs.map((paragraph, index) => {
            const questionNumbers = getQuestionNumbersForParagraph(paragraph.id);
            const prevQuestionNumbers = index > 0
              ? getQuestionNumbersForParagraph(chunk.paragraphs[index - 1].id)
              : [];

            // Check if this paragraph starts a new context (different question set than previous)
            const isNewContext = index > 0 &&
              questionNumbers.length > 0 &&
              prevQuestionNumbers.length > 0 &&
              questionNumbers[0] !== prevQuestionNumbers[0];

            // Check if this paragraph requires special two-column rendering
            const needsTwoColumn = examType && level &&
              requiresTwoColumnRendering(examType, level, chunk.id, paragraph.content);

            if (needsTwoColumn) {
              const { left, right } = splitContextBySeparator(paragraph.content);
              // For two-column layout, show question badges only on the left column
              // Both columns are part of the same context, so one badge is enough
              const leftQuestionNumbers = questionNumbers;
              const rightQuestionNumbers: number[] = []; // No badges on right column

              return (
                <div key={paragraph.id} data-paragraph-id={paragraph.id} className="relative">
                  {isNewContext && (
                    <div className="my-6 sm:my-8 border-t-2 border-border/60" />
                  )}
                  <TwoColumnContext
                    leftContent={left}
                    rightContent={right}
                    textSizeClass={textSizeClasses[textSize]}
                    isHighlighted={paragraph.id === highlightedParagraphId}
                    highlighterActive={highlighterActive}
                    leftQuestionNumbers={leftQuestionNumbers}
                    rightQuestionNumbers={rightQuestionNumbers}
                    paragraphId={paragraph.id}
                    leftHighlights={highlights[`${paragraph.id}-left`] || []}
                    rightHighlights={highlights[`${paragraph.id}-right`] || []}
                    onAddHighlight={onAddHighlight}
                    onQuestionBadgeClick={onContextQuestionSelect}
                    activeQuestionId={activeQuestionId}
                  />
                </div>
              );
            }

            return (
              <div key={paragraph.id} data-paragraph-id={paragraph.id} className="relative">
                {isNewContext && (
                  <div className="my-6 sm:my-8 border-t-2 border-border/60" />
                )}
                <ParagraphBlock
                  paragraph={paragraph}
                  isHighlighted={paragraph.id === highlightedParagraphId}
                  textSizeClass={textSizeClasses[textSize]}
                  highlighterActive={highlighterActive}
                  questionNumbers={questionNumbers}
                  highlights={highlights[String(paragraph.id)] || []}
                  onAddHighlight={onAddHighlight}
                  onContextQuestionSelect={onContextQuestionSelect}
                  activeQuestionId={activeQuestionId}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Footer */}
      <SimulationChunkNavigation
        currentIndex={currentChunkIndex}
        totalChunks={totalChunks}
        onPrevious={() => onNavigate('prev')}
        onNext={() => onNavigate('next')}
      />
    </div>
  );
}

interface ParagraphBlockProps {
  readonly paragraph: Paragraph;
  readonly isHighlighted: boolean;
  readonly textSizeClass: string;
  readonly highlighterActive: boolean;
  readonly questionNumbers: number[];
  readonly highlights: { start: number; end: number }[];
  readonly onAddHighlight: (paragraphId: number, start: number, end: number) => void;
  readonly onContextQuestionSelect?: (questionId: number) => void;
  readonly activeQuestionId?: number | null;
}

/**
 * Renders text content with highlight marks applied.
 * Splits the content into segments based on highlight ranges.
 */
function renderHighlightedText(
  content: string,
  highlights: { start: number; end: number }[]
): React.ReactNode {
  if (highlights.length === 0) {
    return content;
  }

  // Sort highlights by start position
  const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);

  const segments: React.ReactNode[] = [];
  let currentIndex = 0;

  for (const highlight of sortedHighlights) {
    // Add non-highlighted text before this highlight
    if (highlight.start > currentIndex) {
      segments.push(
        <span key={`text-${currentIndex}`}>
          {content.slice(currentIndex, highlight.start)}
        </span>
      );
    }

    // Add highlighted text
    const highlightStart = Math.max(highlight.start, currentIndex);
    const highlightEnd = Math.min(highlight.end, content.length);
    if (highlightEnd > highlightStart) {
      segments.push(
        <mark
          key={`highlight-${highlightStart}`}
          className="bg-yellow-300/60 dark:bg-yellow-500/40 px-0.5 rounded-sm"
        >
          {content.slice(highlightStart, highlightEnd)}
        </mark>
      );
    }

    currentIndex = Math.max(currentIndex, highlightEnd);
  }

  // Add remaining non-highlighted text
  if (currentIndex < content.length) {
    segments.push(
      <span key={`text-${currentIndex}`}>
        {content.slice(currentIndex)}
      </span>
    );
  }

  return <>{segments}</>;
}

function ParagraphBlock({
  paragraph,
  isHighlighted,
  textSizeClass,
  highlighterActive,
  questionNumbers,
  highlights,
  onAddHighlight,
  onContextQuestionSelect,
  activeQuestionId,
}: ParagraphBlockProps) {
  const paragraphRef = useRef<HTMLParagraphElement>(null);

  /**
   * Handle text selection when highlighter is active.
   * Calculates character offsets relative to the paragraph content.
   */
  const handleMouseUp = useCallback(() => {
    if (!highlighterActive) {
      return;
    }

    const selection = globalThis.getSelection();
    if (!selection || selection.isCollapsed || !paragraphRef.current) {
      return;
    }

    // Check if selection is within this paragraph
    const range = selection.getRangeAt(0);
    if (!paragraphRef.current.contains(range.commonAncestorContainer)) {
      return;
    }

    // Calculate the start and end offsets relative to the paragraph text content
    const paragraphText = paragraph.content;
    const selectedText = selection.toString();

    if (!selectedText.trim()) {
      return;
    }

    // Find the position of the selected text in the paragraph
    // We need to calculate offsets based on the DOM structure
    const preCaretRange = document.createRange();
    preCaretRange.selectNodeContents(paragraphRef.current);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    const start = preCaretRange.toString().length;
    const end = start + selectedText.length;

    // Validate offsets are within bounds
    if (start >= 0 && end <= paragraphText.length && start < end) {
      onAddHighlight(paragraph.id, start, end);
    }

    // Clear selection after highlighting
    selection.removeAllRanges();
  }, [highlighterActive, paragraph.id, paragraph.content, onAddHighlight]);

  useEffect(() => {
    // We use a manual event listener instead of onMouseUp prop to satisfy accessibility linting.
    // Text selection for highlighting is a specific mouse interaction that shouldn't
    // change the semantic role of the paragraph for screen readers.
    const el = paragraphRef.current;
    if (!el || !highlighterActive) return;

    el.addEventListener('mouseup', handleMouseUp);
    return () => {
      el.removeEventListener('mouseup', handleMouseUp);
    };
  }, [highlighterActive, handleMouseUp]);


  return (
    <div
      className={cn(
        "relative rounded-lg transition-all duration-500",
        isHighlighted && "ring-2 ring-primary ring-offset-2",
        highlighterActive && "cursor-text select-text"
      )}
    >
      {/* Question Number Badges */}
      {questionNumbers.length > 0 && (
        <div className="absolute -left-[26px] sm:-left-12 top-1 flex flex-col gap-1.5 z-10">
          {questionNumbers.map((qNum) => {
            const badgeClass = cn(
              "w-5 h-5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold transition-all shadow-sm shrink-0",
              "bg-primary text-primary-foreground border border-primary/20",
              "hover:scale-110",
              onContextQuestionSelect
                ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                : "cursor-default"
            );
            if (onContextQuestionSelect) {
              return (
                <button
                  key={qNum}
                  type="button"
                  className={cn(badgeClass, "relative before:absolute before:content-[''] before:-inset-3 before:rounded-full")}
                  aria-label={`Go to question ${qNum}`}
                  aria-current={activeQuestionId === qNum ? 'true' : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    onContextQuestionSelect(qNum);
                  }}
                >
                  {qNum}
                </button>
              );
            }
            return (
              <span key={qNum} className={badgeClass}>
                {qNum}
              </span>
            );
          })}
        </div>
      )}

      <p
        ref={paragraphRef}
        className={cn(
          "text-foreground whitespace-pre-line pl-1 sm:pl-0",
          textSizeClass,
          isHighlighted && "text-foreground font-medium"
        )}
      >
        {renderHighlightedText(paragraph.content, highlights)}
      </p>
    </div>
  );
}
